import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface AccentColors {
  dark: string;
  light: string;
}

interface AccentColorContextValue {
  colors: AccentColors;
  setDarkAccent: (c: string) => void;
  setLightAccent: (c: string) => void;
  accent: string; // current accent based on theme
}

const STORAGE_KEY = 'stingray_accent_colors';

const DEFAULT_COLORS: AccentColors = {
  dark: '#0EA5E9',   // sky-500 — nice blue for dark theme
  light: '#0EA5E9',  // same default for light
};

const AccentColorContext = createContext<AccentColorContextValue>({
  colors: DEFAULT_COLORS,
  setDarkAccent: () => {},
  setLightAccent: () => {},
  accent: DEFAULT_COLORS.dark,
});

interface Props {
  children: ReactNode;
  theme: 'dark' | 'light';
}

export function AccentColorProvider({ children, theme }: Props) {
  const [colors, setColors] = useState<AccentColors>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? { ...DEFAULT_COLORS, ...JSON.parse(saved) } : DEFAULT_COLORS;
    } catch {
      return DEFAULT_COLORS;
    }
  });

  const accent = theme === 'dark' ? colors.dark : colors.light;

  useEffect(() => {
    document.documentElement.style.setProperty('--accent', accent);
    // Compute hover (slightly darker)
    document.documentElement.style.setProperty('--accent-hover', accent);
  }, [accent]);

  const save = (next: AccentColors) => {
    setColors(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  };

  const setDarkAccent = (c: string) => save({ ...colors, dark: c });
  const setLightAccent = (c: string) => save({ ...colors, light: c });

  return (
    <AccentColorContext.Provider value={{ colors, setDarkAccent, setLightAccent, accent }}>
      {children}
    </AccentColorContext.Provider>
  );
}

export const useAccentColor = () => useContext(AccentColorContext);
